// src/rbac.ts
var PICKER_PERMISSIONS = [
  "orders:read",
  "products:read",
  "pick:execute",
  // Placement (расстановка по ячейкам) is warehouse-floor work — pickers do it.
  "cells:bind"
];
var MANAGER_PERMISSIONS = [
  // Full WMS set (superset of picker).
  ...PICKER_PERMISSIONS,
  "pick:override",
  "receive:execute",
  "shipments:create",
  "products:create",
  // Admin dashboard, minus settings and client-credential management.
  "clients:read",
  "clients:manage",
  "orders:read:all",
  "orders:manage",
  "catalog:read:admin",
  "sync:manage",
  // Racking layout is a back-office decision — floor staff only bind into it.
  "cells:manage",
  "audit:read",
  "reports:view",
  "staff:manage",
  "shipments:read:all"
];
var ROLE_PERMISSIONS = {
  customer: ["storefront:use"],
  picker: PICKER_PERMISSIONS,
  manager: MANAGER_PERMISSIONS,
  admin: [...MANAGER_PERMISSIONS, "settings:manage", "clients:credentials", "reports:margin"],
  driver: ["orders:read", "products:read", "pick:execute", "delivery:execute"]
};
function permissionsFor(role) {
  return ROLE_PERMISSIONS[role] ?? [];
}
function hasPermission(principal, permission) {
  if (!principal) return false;
  return (ROLE_PERMISSIONS[principal.role] ?? []).includes(permission);
}

// src/error-codes.ts
var ERROR_CODES = [
  // Auth
  "invalid_credentials",
  "account_pending",
  "account_rejected",
  "rate_limited",
  "token_expired",
  "token_invalid",
  "refresh_reused",
  "no_wms_access",
  "forbidden",
  "unauthorized",
  // Validation / generic
  "validation_error",
  "not_found",
  "conflict",
  "internal_error",
  // Registration
  "phone_taken",
  "email_taken",
  "login_taken",
  // Orders
  "cart_empty",
  "stock_changed",
  "price_changed",
  "product_unavailable",
  "no_counterparty",
  "ms_push_failed",
  // Sync / settings
  "sync_already_running",
  "moysklad_not_configured",
  "moysklad_error",
  // Notifications (Telegram digest)
  "telegram_not_configured",
  "telegram_invalid_token",
  // WMS
  "order_not_shippable",
  "loader_required",
  "driver_required",
  "group_mixed_agents",
  // WMS shared pick sessions (real-time collaborative picking)
  "session_shipped",
  "already_shipped",
  // WMS act-as-driver (staff operating on a driver's behalf)
  "acting_driver_required",
  "invalid_acting_driver",
  // WMS photo-order flow
  "ai_unavailable",
  "ai_error",
  // Staff
  "staff_login_taken",
  // Warehouse cells
  "cell_name_taken",
  "cell_barcode_taken",
  "cell_in_use",
  "cell_not_found",
  // Warehouse map (layout constructor)
  "layout_section_not_found",
  "layout_spot_out_of_range",
  // Accountant integration (storefront pricing + naqt/terminal/rasxod)
  "no_counterparty_link",
  // customer has no MoySklad counterparty → cannot reach the accountant
  "store_not_mapped",
  // counterparty maps to no active accountant Store
  "shipment_not_ready",
  // накладная not yet synced into the accountant
  "accountant_unavailable"
  // the accountant service is unreachable / not configured
];

// src/contracts/common.ts
import { z } from "zod";
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20)
});
var kopecksSchema = z.number().int().min(0);
var idSchema = z.string().min(1);

// src/contracts/auth.ts
import { z as z2 } from "zod";
var loginSchema = z2.object({
  /** Phone, email, or admin-issued login slug. Resolved in that priority order. */
  identifier: z2.string().trim().min(1, "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0442\u0435\u043B\u0435\u0444\u043E\u043D, email \u0438\u043B\u0438 \u043B\u043E\u0433\u0438\u043D"),
  password: z2.string().min(1, "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043F\u0430\u0440\u043E\u043B\u044C")
});
var registerSchema = z2.object({
  firstName: z2.string().trim().min(1).max(60),
  lastName: z2.string().trim().min(1).max(60),
  phone: z2.string().trim().min(1),
  email: z2.string().trim().email().optional().or(z2.literal("")),
  password: z2.string().min(8, "\u041C\u0438\u043D\u0438\u043C\u0443\u043C 8 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432").max(128),
  companyName: z2.string().trim().max(200).optional(),
  inn: z2.string().trim().max(20).optional(),
  preferredLocale: z2.enum(["ru", "uz"]).default("ru")
});
var staffLoginSchema = z2.object({
  /** MoySklad employee login — the company suffix is optional ("user" or "user@company"). */
  login: z2.string().trim().min(1),
  password: z2.string().min(1)
});
var refreshSchema = z2.object({
  /** Native clients may pass the token in the body; browsers use the cookie. */
  refreshToken: z2.string().optional()
});

// src/contracts/users.ts
import { z as z3 } from "zod";
var updateProfileSchema = z3.object({
  firstName: z3.string().trim().min(1).max(60).optional(),
  lastName: z3.string().trim().min(1).max(60).optional(),
  email: z3.string().trim().email().optional().or(z3.literal("")),
  companyName: z3.string().trim().max(200).optional(),
  inn: z3.string().trim().max(20).optional(),
  preferredLocale: z3.enum(["ru", "uz"]).optional(),
  latitude: z3.number().min(-90).max(90).nullable().optional(),
  longitude: z3.number().min(-180).max(180).nullable().optional()
}).refine((v) => v.latitude === void 0 === (v.longitude === void 0), {
  message: "latitude and longitude must be sent together"
}).refine((v) => v.latitude === null === (v.longitude === null), {
  message: "latitude and longitude must be cleared together"
});
var changePasswordSchema = z3.object({
  currentPassword: z3.string().min(1),
  newPassword: z3.string().min(8, "\u041C\u0438\u043D\u0438\u043C\u0443\u043C 8 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432").max(128)
});
var setPhoneSchema = z3.object({
  phone: z3.string().trim().min(1)
});

// src/contracts/admin-users.ts
import { z as z4 } from "zod";
var clientsQuerySchema = paginationQuerySchema.extend({
  status: z4.enum(["PENDING", "APPROVED", "REJECTED"]).optional(),
  /** Free text over name, phone, email, login, company, counterparty. */
  search: z4.string().trim().optional(),
  driverId: z4.string().optional(),
  /** `true` = only clients with no MoySklad counterparty linked. */
  unlinked: z4.coerce.boolean().optional()
});
var counterpartyIntentSchema = z4.discriminatedUnion("mode", [
  z4.object({ mode: z4.literal("link"), msId: z4.string().min(1) }),
  z4.object({ mode: z4.literal("create") }),
  z4.object({ mode: z4.literal("none") })
]);
var approveClientSchema = z4.object({
  counterparty: counterpartyIntentSchema.optional()
});
var rejectClientSchema = z4.object({
  reason: z4.string().trim().min(1, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u0443").max(500)
});
var updateClientSchema = z4.object({
  firstName: z4.string().trim().min(1).max(60).optional(),
  /** Empty allowed: bulk-created clients carry the whole name in firstName. */
  lastName: z4.string().trim().max(60).optional(),
  /** Canonicalized and uniqueness-checked server-side. Never cleared — it is a login identifier. */
  phone: z4.string().trim().min(1).optional(),
  /** Empty string clears the email. */
  email: z4.string().trim().email().optional().or(z4.literal("")),
  canViewPrices: z4.boolean().optional(),
  /** Show the shop's last nakladnoy issue price (riba-accountant) instead of the MoySklad price. Display only. */
  showIssuePrice: z4.boolean().optional(),
  /** Assigned delivery driver (StaffMember of type DRIVER); null clears it. */
  driverId: z4.string().nullable().optional(),
  companyName: z4.string().trim().max(200).nullable().optional(),
  inn: z4.string().trim().max(20).nullable().optional(),
  /** MoySklad sale price type for this client; null reverts to the settings default. */
  priceTypeId: z4.string().min(1).nullable().optional(),
  /** Delivery point on the map; send both or neither, null clears both. */
  latitude: z4.number().min(-90).max(90).nullable().optional(),
  longitude: z4.number().min(-180).max(180).nullable().optional()
}).refine((v) => v.latitude === void 0 === (v.longitude === void 0), {
  message: "latitude and longitude must be sent together"
}).refine((v) => v.latitude === null === (v.longitude === null), {
  message: "latitude and longitude must be cleared together"
});
var changeLoginSchema = z4.object({
  login: z4.string().trim().min(3).max(64).regex(/^[a-z0-9-]+$/, "\u0422\u043E\u043B\u044C\u043A\u043E \u0441\u0442\u0440\u043E\u0447\u043D\u044B\u0435 \u043B\u0430\u0442\u0438\u043D\u0441\u043A\u0438\u0435 \u0431\u0443\u043A\u0432\u044B, \u0446\u0438\u0444\u0440\u044B \u0438 \u0434\u0435\u0444\u0438\u0441")
});
var changeCredentialsSchema = z4.object({
  login: z4.string().trim().min(3).max(64).regex(/^[a-z0-9-]+$/).optional(),
  password: z4.string().min(8).max(128).optional()
});
var bulkCreateClientsSchema = z4.object({
  names: z4.array(z4.string().trim().min(1).max(200)).min(1).max(200)
});
var bulkIdsSchema = z4.object({
  userIds: z4.array(z4.string().min(1).max(128)).min(1).max(500)
});
var bulkPriceVisibilitySchema = bulkIdsSchema.extend({ canViewPrices: z4.boolean() });
var bulkSetPasswordSchema = bulkIdsSchema.extend({
  // Admin-chosen shared password, handed out manually — a sane minimum length
  // is the only rule (no complexity requirements like self-service change).
  newPassword: z4.string().min(8).max(128)
});
var bulkAssignDriverSchema = bulkIdsSchema.extend({
  driverId: z4.string().min(1).nullable()
});
var bulkPriceTypeSchema = bulkIdsSchema.extend({
  priceTypeId: z4.string().min(1).nullable()
});
var counterpartySearchSchema = z4.object({
  q: z4.string().trim().min(1)
});

// src/contracts/staff.ts
import { z as z5 } from "zod";
var staffTypeSchema = z5.enum(["DRIVER", "LOADER"]);
var staffQuerySchema = z5.object({
  type: staffTypeSchema.optional(),
  isActive: z5.coerce.boolean().optional(),
  search: z5.string().trim().optional()
});
var phoneSchema = z5.string().trim().regex(/^\+?[\d\s()-]{5,31}$/, "\u041D\u0435\u0432\u0435\u0440\u043D\u044B\u0439 \u0444\u043E\u0440\u043C\u0430\u0442 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430");
var createStaffSchema = z5.object({
  type: staffTypeSchema,
  name: z5.string().trim().min(1, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0438\u043C\u044F").max(120),
  phone: phoneSchema.optional().or(z5.literal("")),
  /** Drivers only — required, because a driver with no login cannot work. */
  login: z5.string().trim().min(3).max(64).regex(/^[a-z0-9-]+$/, "\u0422\u043E\u043B\u044C\u043A\u043E \u0441\u0442\u0440\u043E\u0447\u043D\u044B\u0435 \u043B\u0430\u0442\u0438\u043D\u0441\u043A\u0438\u0435 \u0431\u0443\u043A\u0432\u044B, \u0446\u0438\u0444\u0440\u044B \u0438 \u0434\u0435\u0444\u0438\u0441").optional(),
  password: z5.string().min(6).max(128).optional()
}).superRefine((input, ctx) => {
  if (input.type === "DRIVER" && (!input.login || !input.password)) {
    ctx.addIssue({
      code: "custom",
      path: ["login"],
      message: "\u0412\u043E\u0434\u0438\u0442\u0435\u043B\u044E \u043D\u0443\u0436\u043D\u044B \u043B\u043E\u0433\u0438\u043D \u0438 \u043F\u0430\u0440\u043E\u043B\u044C \u0434\u043B\u044F \u0432\u0445\u043E\u0434\u0430 \u0432 WMS"
    });
  }
  if (input.type === "LOADER" && (input.login || input.password)) {
    ctx.addIssue({
      code: "custom",
      path: ["login"],
      message: "\u0413\u0440\u0443\u0437\u0447\u0438\u043A \u043D\u0435 \u0432\u0445\u043E\u0434\u0438\u0442 \u0432 \u0441\u0438\u0441\u0442\u0435\u043C\u0443 \u2014 \u043B\u043E\u0433\u0438\u043D \u043D\u0435 \u043D\u0443\u0436\u0435\u043D"
    });
  }
});
var updateStaffSchema = z5.object({
  name: z5.string().trim().min(1, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0438\u043C\u044F").max(120).optional(),
  phone: phoneSchema.nullable().optional().or(z5.literal("")),
  isActive: z5.boolean().optional()
});
var staffCredentialsSchema = z5.object({
  login: z5.string().trim().min(3).max(64).regex(/^[a-z0-9-]+$/).optional(),
  password: z5.string().min(6).max(128)
});

// src/contracts/catalog.ts
import { z as z6 } from "zod";
var sortOptionSchema = z6.enum(["name_asc", "name_desc", "price_asc", "price_desc"]).default("name_asc");
var productsQuerySchema = paginationQuerySchema.extend({
  categoryId: z6.string().optional(),
  search: z6.string().trim().optional(),
  sort: sortOptionSchema,
  inStock: z6.coerce.boolean().optional(),
  /** Admin catalog only: active (default) / hidden (gone from MoySklad) / all.
   *  The public storefront route ignores it — visibility rules live there. */
  status: z6.enum(["active", "hidden", "all"]).optional(),
  /** Admin catalog only: products bound to this warehouse cell (map drill-down). */
  cellId: z6.string().optional()
});
var featuredQuerySchema = z6.object({
  limit: z6.coerce.number().int().min(1).max(24).default(4)
});
var batchProductsSchema = z6.object({
  ids: z6.array(idSchema).min(1).max(100)
});

// src/contracts/cart.ts
import { z as z7 } from "zod";
var addToCartSchema = z7.object({
  productId: z7.string().min(1),
  /** ALWAYS in base units (kg or pieces) — never a pack count. */
  quantity: z7.number().positive().max(1e5),
  /** Selected MoySklad pack; null/absent = base unit. */
  packId: z7.string().nullable().optional()
});
var setCartQtySchema = z7.object({
  quantity: z7.number().positive().max(1e5),
  packId: z7.string().nullable().optional()
});
var mergeCartSchema = z7.object({
  items: z7.array(addToCartSchema).max(200)
});

// src/contracts/orders.ts
import { z as z8 } from "zod";
var placeOrderSchema = z8.object({
  comment: z8.string().trim().max(1e3).optional()
});
var ordersQuerySchema = paginationQuerySchema.extend({
  status: z8.enum(["NEW", "SENT_TO_MS", "MS_FAILED", "CONFIRMED", "DELIVERED", "CANCELLED", "DELETED_IN_MS"]).optional(),
  userId: z8.string().optional(),
  search: z8.string().trim().optional()
});

// src/contracts/settings.ts
import { z as z9 } from "zod";
var setTokenSchema = z9.object({
  token: z9.string().trim().min(10, "\u0422\u043E\u043A\u0435\u043D \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u043A\u043E\u0440\u043E\u0442\u043A\u0438\u0439")
});
var saveMoyskladConfigSchema = z9.object({
  priceTypeId: z9.string().nullable().optional(),
  priceTypeName: z9.string().nullable().optional(),
  organizationId: z9.string().nullable().optional(),
  organizationName: z9.string().nullable().optional(),
  storeId: z9.string().nullable().optional(),
  storeName: z9.string().nullable().optional()
});
var setCheckStockSchema = z9.object({
  checkStock: z9.boolean()
});
var setHideInactiveProductsSchema = z9.object({
  hideInactiveProducts: z9.boolean()
});
var setPickCategoryOrderSchema = z9.object({
  order: z9.array(z9.string().trim().min(1).max(200)).max(500)
});

// src/contracts/sync.ts
import { z as z10 } from "zod";
var syncHistoryQuerySchema = z10.object({
  limit: z10.coerce.number().int().min(1).max(100).default(20)
});

// src/contracts/wms.ts
import { z as z11 } from "zod";
var wmsLoginSchema = z11.object({
  /** A driver's local login, or a MoySklad employee login. */
  login: z11.string().trim().min(1),
  password: z11.string().min(1)
});
var pickQueueQuerySchema = z11.object({
  /** Filter by MoySklad order state id. */
  state: z11.string().optional()
});
var createShipmentSchema = z11.object({
  /** Picked base-unit quantity per assortment id. */
  pickedByAssortment: z11.record(z11.string(), z11.number().min(0)),
  /**
   * Advisory: the server recomputes completeness from the picked quantities and
   * never trusts this value.
   */
  complete: z11.boolean().default(false),
  /** Off-order extras need their MoySklad meta so the demand can include them. */
  extraMeta: z11.record(z11.string(), z11.object({ href: z11.string(), type: z11.string() })).optional(),
  /**
   * Who physically collected the load (грузчик). REQUIRED for everyone — the
   * server refuses the otgruzka without it (`loader_required`), and MoySklad's
   * «Грузчик» field is filled from it. Optional here only so old clients get a
   * clean 400 instead of a schema error.
   */
  loaderId: z11.string().optional(),
  /**
   * Who delivers (доставщик). REQUIRED from warehouse staff
   * (`driver_required`); ignored when a driver ships — they deliver themselves.
   */
  driverId: z11.string().optional(),
  /**
   * The shared pick-session key (see pick-sync.ts). When set and the session
   * has synced contributions, the server ships the MERGED per-actor state and
   * ignores pickedByAssortment/extraMeta — a device that missed a colleague's
   * last scans still ships everything. Old clients omit it and keep the
   * client-map path.
   */
  sessionKey: z11.string().max(120).optional()
});
var createGroupShipmentSchema = createShipmentSchema.extend({
  // Matches MAX_GROUP_ORDERS on the server: a client's same-day tile carries
  // every one of their orders, and a cap below that makes the tile unopenable
  // rather than merely large.
  orderIds: z11.array(z11.string().min(1)).min(1).max(50)
});
var groupQuerySchema = z11.object({ ids: z11.string().min(1) });
var ACTING_DRIVER_HEADER = "x-acting-driver";
var extractImageSchema = z11.object({
  image: z11.string().min(100).max(14e6),
  mediaType: z11.enum(["image/jpeg", "image/png", "image/webp"])
});
var extractShipmentSchema = z11.object({
  images: z11.array(extractImageSchema).min(1).max(6)
});
var createDemandSchema = z11.object({
  agentMetaHref: z11.string().min(1),
  positions: z11.array(
    z11.object({
      assortmentId: z11.string().min(1),
      metaHref: z11.string().min(1),
      metaType: z11.string().min(1),
      /** Base units; fractional for weight goods. */
      quantity: z11.number().finite().positive(),
      /** Per-unit price in sums — the server converts to kopecks exactly once. */
      pricePerUnit: z11.number().finite().nonnegative().optional()
    })
  ).min(1)
});
var createProductSchema = z11.object({
  name: z11.string().trim().min(1).max(255),
  /** Omitted → created at the catalog root, like MoySklad. */
  productFolderId: z11.string().min(1).optional(),
  /** Omitted → MoySklad's default («шт»). */
  uomId: z11.string().min(1).optional(),
  /** Весовой товар. Omitted → MoySklad's default (false). */
  weighed: z11.boolean().optional(),
  /** A single package (упаковка): its uom + base units per pack. */
  pack: z11.object({
    uomId: z11.string().min(1),
    quantity: z11.number().finite().positive()
  }).optional(),
  /** Downscaled client-side; ~7M chars ≈ 5 MB is plenty for a product photo. */
  image: z11.object({
    filename: z11.string().trim().min(1).max(255),
    content: z11.string().min(100).max(7e6)
  }).optional()
});
var counterpartyQuerySchema = z11.object({
  q: z11.string().trim().default("")
});

// src/contracts/cells.ts
import { z as z12 } from "zod";
var cellNameSchema = z12.string().trim().min(1).max(40);
var createCellSchema = z12.object({
  name: cellNameSchema,
  zoneName: z12.string().trim().max(60).nullable().optional(),
  barcode: z12.string().trim().max(60).nullable().optional()
});
var updateCellSchema = createCellSchema.partial().extend({
  isActive: z12.boolean().optional()
});
var bindProductCellSchema = z12.object({
  cellId: z12.string().min(1).nullable()
});
var setCellOrderSchema = z12.object({
  cellIds: z12.array(z12.string().min(1)).max(2e3)
});

// src/contracts/pick-sync.ts
import { z as z13 } from "zod";
var PICK_SESSION_KEY_RE = /^(?:[0-9a-f-]{36}|group:[0-9a-f-]{36}:\d{4}-\d{2}-\d{2})$/;
var pickSyncOpSchema = z13.object({
  assortmentId: z13.string().min(1).max(64),
  /** The actor's OWN absolute contribution in base units — not a delta. */
  qty: z13.number().min(0).finite(),
  /** The gather-pass «ptichka» mark; displayed as OR across actors. */
  checked: z13.boolean().default(false),
  /** Actor-device monotonic op counter; the server ignores seq <= stored. */
  seq: z13.number().int().min(1),
  /**
   * A deliberate downward CORRECTION of the whole line («там 9, должно быть
   * 8»): the sender's register becomes the full amount and every colleague's
   * register on this line drops to zero. Their seq stays untouched, so scans
   * they make AFTER the correction still add on top — only the corrected-away
   * value stays dead.
   */
  override: z13.boolean().default(false)
});
var pickSyncExtraSchema = z13.object({
  assortmentId: z13.string().min(1).max(64),
  /**
   * ProductLookup snapshot so other clients render the extra without re-doing
   * the lookup, and the ship path gets metaHref/metaType from the session.
   */
  product: z13.object({
    assortmentId: z13.string().min(1),
    name: z13.string(),
    uom: z13.string(),
    isWeight: z13.boolean(),
    metaHref: z13.string().min(1),
    metaType: z13.string().min(1)
  }).passthrough(),
  /** When the extra substitutes an order line («килька 9» → «килька 11»). */
  replacesId: z13.string().optional(),
  replacesName: z13.string().optional()
});
var pickSyncPushSchema = z13.object({
  /** Short per-device id; the server acts as actorId = "<principalId>#<deviceId>". */
  deviceId: z13.string().regex(/^[A-Za-z0-9-]{4,36}$/),
  /** Session membership (single order → [orderId]); refreshed on every push. */
  // Same ceiling as a group shipment: a session covers exactly the orders the
  // tile does, so a lower cap here would let a big group open and then refuse
  // every scan.
  orderIds: z13.array(z13.string().min(1)).min(1).max(50),
  ops: z13.array(pickSyncOpSchema).max(300).default([]),
  addExtras: z13.array(pickSyncExtraSchema).max(50).default([]),
  removeExtras: z13.array(z13.string().min(1)).max(50).default([])
});

// src/contracts/audit.ts
import { z as z14 } from "zod";
var auditQuerySchema = paginationQuerySchema.extend({
  actorId: z14.string().optional(),
  action: z14.string().optional(),
  targetId: z14.string().optional()
});

// src/contracts/reports.ts
import { z as z15 } from "zod";
var receivablesQuerySchema = z15.object({
  /** How far back to scan demands. Debt older than the window still shows —
   * MoySklad returns only demands inside it, so widen when totals look short. */
  windowDays: z15.coerce.number().int().min(7).max(365).default(180),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
});
var isoDateSchema = z15.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var profitQuerySchema = z15.object({
  by: z15.enum(["product", "counterparty"]).default("product"),
  /** Inclusive period bounds; defaults to the last 30 days. */
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var turnoverQuerySchema = z15.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var dailyOrdersQuerySchema = z15.object({
  /** Calendar day (Tashkent, UTC+5) to aggregate; defaults to today. */
  date: isoDateSchema.optional(),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
});
var expiryQuerySchema = z15.object({
  /** How far back to scan приёмки. Stock older than this shows as "unknown age". */
  windowDays: z15.coerce.number().int().min(7).max(120).default(60),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
});
var shelfLifeUpdateSchema = z15.object({
  /** Days; null clears the norm. */
  days: z15.number().int().min(1).max(365).nullable()
});
var purchasePlanQuerySchema = z15.object({
  /** Sales window feeding the avg-daily-outflow estimate. */
  windowDays: z15.coerce.number().int().min(7).max(120).default(30),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
});
var purchaseNormsUpdateSchema = z15.object({
  /** Days; null clears (product level: null = inherit the category). */
  leadTimeDays: z15.number().int().min(0).max(90).nullable(),
  safetyStockDays: z15.number().int().min(0).max(90).nullable()
});
var abcXyzQuerySchema = z15.object({
  weeks: z15.coerce.number().int().min(4).max(26).default(12),
  /** Any non-empty value bypasses the server-side cache. */
  refresh: z15.string().optional()
});
var shrinkageQuerySchema = z15.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var shrinkageNormUpdateSchema = z15.object({
  /** Percent of received weight; null clears (product level: null = inherit). */
  pct: z15.number().min(0).max(100).nullable()
});
var returnsQuerySchema = z15.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var suppliersQuerySchema = z15.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var inventoryQuerySchema = z15.object({
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  refresh: z15.string().optional()
}).refine((q) => !q.dateFrom || !q.dateTo || q.dateFrom <= q.dateTo, {
  message: "dateFrom must not be after dateTo"
});
var deadStockQuerySchema = z15.object({
  /** Minimum days without a приёмка/отгрузка to count as dead. */
  minDays: z15.coerce.number().int().min(1).max(730).default(30)
});

// src/contracts/notifications.ts
import { z as z16 } from "zod";
var telegramTokenSchema = z16.object({
  token: z16.string().trim().max(200)
});
var notificationSettingsUpdateSchema = z16.object({
  /** Chat/group the digest goes to. Null clears. */
  telegramChatId: z16.string().trim().min(1).max(64).nullable().optional(),
  expiryDigestEnabled: z16.boolean().optional(),
  /** Hour of day in Asia/Tashkent (UTC+5). */
  expiryDigestHour: z16.number().int().min(0).max(23).optional()
});

// src/contracts/pos-bridge.ts
import { z as z17 } from "zod";
var posBridgeConfigUpdateSchema = z17.object({
  enabled: z17.boolean().optional(),
  /** POS API base URL, no trailing slash. Empty string / null clears. */
  webhookBaseUrl: z17.string().trim().max(500).refine((v) => v === "" || /^https?:\/\/[^\s/]+/.test(v), "Invalid URL").nullable().optional(),
  /** Source key issued by the POS console. Empty string / null clears. */
  sourceKey: z17.string().trim().max(200).nullable().optional(),
  /** MoySklad counterparty ids of the owner's own shops. */
  shopCounterpartyIds: z17.array(z17.string().trim().min(1).max(64)).max(500).optional()
});
var posBridgeSecretSchema = z17.object({
  secret: z17.string().trim().max(500)
});

// src/contracts/warehouse-layout.ts
import { z as z18 } from "zod";
var layoutZoneSchema = z18.object({
  kind: z18.enum(["buffer", "gate"]),
  xCm: z18.number().int().min(0).max(1e4),
  yCm: z18.number().int().min(0).max(1e4),
  wCm: z18.number().int().min(1).max(1e4),
  hCm: z18.number().int().min(1).max(1e4),
  label: z18.string().trim().max(40).optional()
});
var saveSectionSchema = z18.object({
  id: z18.string().min(1).optional(),
  label: z18.string().trim().min(1).max(60),
  xCm: z18.number().int().min(0).max(1e4),
  yCm: z18.number().int().min(0).max(1e4),
  /** 0 = beam runs horizontally (west–east), 90 = vertically (north–south). */
  rotation: z18.union([z18.literal(0), z18.literal(90)]),
  lengthCm: z18.number().int().min(50).max(2e3),
  depthCm: z18.number().int().min(30).max(500),
  levels: z18.number().int().min(1).max(6),
  spotsPerLevel: z18.number().int().min(1).max(10)
});
var saveLayoutSchema = z18.object({
  widthCm: z18.number().int().min(200).max(1e4),
  depthCm: z18.number().int().min(200).max(1e4),
  zones: z18.array(layoutZoneSchema).max(50),
  sections: z18.array(saveSectionSchema).max(200)
});
var assignSpotSchema = z18.object({
  sectionId: z18.string().min(1),
  level: z18.number().int().min(0).max(5),
  index: z18.number().int().min(0).max(9),
  cellId: z18.string().min(1).nullable()
});

// src/domain/login-gen.ts
var CYRILLIC_MAP = {
  \u0430: "a",
  \u0431: "b",
  \u0432: "v",
  \u0433: "g",
  \u0434: "d",
  \u0435: "e",
  \u0451: "e",
  \u0436: "zh",
  \u0437: "z",
  \u0438: "i",
  \u0439: "y",
  \u043A: "k",
  \u043B: "l",
  \u043C: "m",
  \u043D: "n",
  \u043E: "o",
  \u043F: "p",
  \u0440: "r",
  \u0441: "s",
  \u0442: "t",
  \u0443: "u",
  \u0444: "f",
  \u0445: "h",
  \u0446: "c",
  \u0447: "ch",
  \u0448: "sh",
  \u0449: "sch",
  \u044A: "",
  \u044B: "y",
  \u044C: "",
  \u044D: "e",
  \u044E: "yu",
  \u044F: "ya"
};
function transliterate(input) {
  return input.toLowerCase().split("").map((ch) => CYRILLIC_MAP[ch] ?? ch).join("");
}
function slugifyLogin(name) {
  const slug = transliterate(name).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug || "user";
}
async function uniqueLogin(base, exists) {
  if (!await exists(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}-${i}`;
    if (!await exists(candidate)) return candidate;
  }
}

// src/domain/search-normalize.ts
var CYRILLIC_TO_LATIN = {
  \u0430: "a",
  \u0431: "b",
  \u0432: "v",
  \u0433: "g",
  \u0434: "d",
  \u0435: "e",
  \u0451: "e",
  \u0436: "zh",
  \u0437: "z",
  \u0438: "i",
  \u0439: "y",
  \u043A: "k",
  \u043B: "l",
  \u043C: "m",
  \u043D: "n",
  \u043E: "o",
  \u043F: "p",
  \u0440: "r",
  \u0441: "s",
  \u0442: "t",
  \u0443: "u",
  \u0444: "f",
  \u0445: "h",
  \u0446: "ts",
  \u0447: "ch",
  \u0448: "sh",
  \u0449: "sch",
  \u044A: "",
  \u044B: "y",
  \u044C: "",
  \u044D: "e",
  \u044E: "yu",
  \u044F: "ya",
  // Uzbek Cyrillic extras
  \u045E: "o",
  \u049B: "q",
  \u0493: "g",
  \u04B3: "h",
  \u0446\u030C: "ts",
  // Cyrillic that looks Latin but isn't (defensive)
  \u0458: "j",
  \u0456: "i",
  \u0457: "i",
  \u0454: "e"
};
function normalizeSearch(input) {
  if (!input) return "";
  let out = "";
  for (const ch of input.toLowerCase()) {
    out += ch in CYRILLIC_TO_LATIN ? CYRILLIC_TO_LATIN[ch] : ch;
  }
  return out.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}
function searchTokens(input) {
  const normalized = normalizeSearch(input);
  return normalized ? normalized.split(" ") : [];
}
function trigramsOf(normalized) {
  const grams = /* @__PURE__ */ new Set();
  for (const word of normalized.split(" ")) {
    if (!word) continue;
    const padded = `  ${word} `;
    for (let i = 0; i <= padded.length - 3; i++) grams.add(padded.slice(i, i + 3));
  }
  return grams;
}
function wordSimilarity(query, text) {
  const nq = normalizeSearch(query);
  return bestWindowSimilarity(
    nq,
    normalizeSearch(text).split(" ").filter(Boolean),
    Math.min(3, (nq ? nq.split(" ").length : 0) + 1)
  );
}
function bestWindowSimilarity(nq, words, maxSpan) {
  const qGrams = trigramsOf(nq);
  if (qGrams.size === 0 || words.length === 0) return 0;
  let best = 0;
  for (let i = 0; i < words.length; i++) {
    for (let span = 1; span <= maxSpan && i + span <= words.length; span++) {
      const wGrams = trigramsOf(words.slice(i, i + span).join(" "));
      let shared = 0;
      for (const g of qGrams) if (wGrams.has(g)) shared++;
      const union = qGrams.size + wGrams.size - shared;
      if (union > 0 && shared / union > best) best = shared / union;
    }
  }
  return best;
}
var FUZZY_THRESHOLD = 0.3;
function searchScore(query, text) {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return 0;
  const hay = normalizeSearch(text);
  if (!hay) return 0;
  const words = hay.split(" ");
  let allExact = true;
  let simSum = 0;
  for (const t of tokens) {
    if (hay.includes(t)) {
      simSum += 1;
      continue;
    }
    const sim = bestWindowSimilarity(t, words, 2);
    if (sim < FUZZY_THRESHOLD) return 0;
    allExact = false;
    simSum += sim;
  }
  if (allExact) {
    return 2 + bestWindowSimilarity(tokens.join(" "), words, Math.min(3, tokens.length + 1));
  }
  return 1 + simSum / tokens.length;
}
function matchesSearch(query, text) {
  return searchScore(query, text) > 0;
}
function rankBySearch(items, query, textOf) {
  if (!query.trim()) return [...items];
  return items.map((item, i) => ({ item, i, score: searchScore(query, textOf(item)) })).filter((x) => x.score > 0).sort((a, b) => b.score - a.score || a.i - b.i).map((x) => x.item);
}

// src/domain/slug.ts
var MAX_SLUG_LENGTH = 80;
function slugifyName(name) {
  const slug = normalizeSearch(name).replace(/ /g, "-");
  if (slug.length <= MAX_SLUG_LENGTH) return slug;
  const cut = slug.slice(0, MAX_SLUG_LENGTH + 1);
  const boundary = cut.lastIndexOf("-");
  return boundary > 0 ? cut.slice(0, boundary) : slug.slice(0, MAX_SLUG_LENGTH);
}
function uniqueSlug(name, taken) {
  const base = slugifyName(name) || "product";
  let candidate = base;
  for (let n = 2; taken.has(candidate); n++) {
    candidate = `${base}-${n}`;
  }
  taken.add(candidate);
  return candidate;
}

// src/domain/packs.ts
function parsePacks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const { msId, name, quantity } = entry;
    if (typeof msId !== "string" || typeof quantity !== "number" || quantity <= 0) return [];
    return [{ msId, name: typeof name === "string" ? name : "", quantity }];
  });
}
function findPack(packs, packId) {
  if (!packId) return null;
  return packs.find((p) => p.msId === packId) ?? null;
}
function baseQuantityFor(pack, packCount) {
  return round3(pack.quantity * packCount);
}
function unitRules(uom) {
  const u = (uom ?? "").toLowerCase();
  const isWeight = u.includes("\u043A\u0433") || u.includes("kg") || u.includes("\u0433\u0440") || u.startsWith("\u0433");
  return isWeight ? { step: 0.5, min: 0.5, decimals: 1 } : { step: 1, min: 1, decimals: 0 };
}
function snapQty(n, step) {
  if (!step) return n;
  return Math.max(0, round3(Math.round(n / step) * step));
}
function lineTotal(priceKopecks, quantity) {
  return Math.round(priceKopecks * quantity);
}
function round3(n) {
  return Math.round(n * 1e3) / 1e3;
}

// src/domain/pack-display.ts
function baseToPacks(base, baseQty) {
  if (!Number.isFinite(baseQty) || baseQty <= 0) return 0;
  return Math.round(base / baseQty * 100) / 100;
}
function formatPacks(n) {
  return String(Math.round(n * 100) / 100);
}

// src/domain/weightBarcode.ts
var DEFAULT_WEIGHT_CONFIG = {
  length: 13,
  prefixes: ["21"],
  // scale labels; fixed product barcodes use "2000000…"
  codeRange: [2, 7],
  weightRange: [7, 12],
  weightDivisor: 1e3
  // weight field is in grams: 00105 → 0.105 kg
};
function parseWeightBarcode(code, cfg = DEFAULT_WEIGHT_CONFIG) {
  if (code.length !== cfg.length) return null;
  if (!/^\d+$/.test(code)) return null;
  if (!cfg.prefixes.some((p) => code.startsWith(p))) return null;
  const rawCode = code.slice(cfg.codeRange[0], cfg.codeRange[1]);
  const rawWeight = code.slice(cfg.weightRange[0], cfg.weightRange[1]);
  const weightKg = Math.round(Number(rawWeight) / cfg.weightDivisor * 1e3) / 1e3;
  if (!Number.isFinite(weightKg) || weightKg <= 0) return null;
  return { productCode: rawCode, weightKg };
}

// src/domain/picking.ts
function normalizeCode(raw) {
  return raw.trim();
}
function round32(n) {
  return Math.round(n * 1e3) / 1e3;
}
function resolveScan(order, rawCode) {
  const code = normalizeCode(rawCode);
  if (!code) return { kind: "unknown", code };
  const fixed = order.barcodeIndex.find((e) => e.code === code);
  if (fixed) {
    return {
      kind: "matched",
      assortmentId: fixed.assortmentId,
      amount: fixed.unitsPerScan,
      label: fixed.label
    };
  }
  const weight = parseWeightBarcode(code);
  if (weight) {
    const line = order.lines.find((l) => l.code === weight.productCode && l.isWeight);
    if (line) {
      return {
        kind: "weight",
        assortmentId: line.assortmentId,
        weightKg: weight.weightKg,
        label: `${weight.weightKg} \u043A\u0433`
      };
    }
  }
  return { kind: "unknown", code };
}
function matchScan(index, rawCode) {
  return resolveScan({ lines: [], barcodeIndex: index }, rawCode);
}
function lineStatus(picked, required, isWeight = false, tolerance = 0) {
  if (isWeight && tolerance > 0) {
    const lo = required * (1 - tolerance);
    const hi = required * (1 + tolerance);
    if (picked < lo) return "under";
    if (picked > hi) return "over";
    return "exact";
  }
  if (picked < required) return "under";
  if (picked > required) return "over";
  return "exact";
}
function stockState(line) {
  if (line.availableStock == null) return "ok";
  if (line.availableStock <= 0 && line.requiredQty > 0) return "none";
  if (line.availableStock < line.requiredQty) return "short";
  return "ok";
}
function effectiveTarget(line) {
  if (line.availableStock == null) return line.requiredQty;
  return Math.min(line.requiredQty, Math.max(0, line.availableStock));
}
function lineStatusFor(line, picked, tolerance, allowOversell = true) {
  const stock = line.availableStock == null ? Number.POSITIVE_INFINITY : Math.max(0, line.availableStock);
  let lo;
  let hi;
  if (allowOversell) {
    lo = Math.min(line.requiredQty, Math.max(stock, picked));
    hi = line.requiredQty;
  } else {
    lo = hi = Math.min(line.requiredQty, stock);
  }
  if (line.isWeight && tolerance > 0) {
    if (picked < lo * (1 - tolerance)) return "under";
    if (picked > hi * (1 + tolerance)) return "over";
    return "exact";
  }
  if (picked < lo) return "under";
  if (picked > hi) return "over";
  return "exact";
}
function isOrderComplete(lines, pickedByAssortment, tolerance = 0) {
  return lines.every(
    (l) => lineStatus(pickedByAssortment[l.assortmentId] ?? 0, l.requiredQty, l.isWeight, tolerance) === "exact"
  );
}
function weightWindow(required, tolerance) {
  return { lo: round32(required * (1 - tolerance)), hi: round32(required * (1 + tolerance)) };
}
function orderProgress(lines, pickedByAssortment, tolerance = 0, allowOversell = true) {
  let greenCount = 0;
  let hasOver = false;
  let hasUnder = false;
  for (const line of lines) {
    const picked = pickedByAssortment[line.assortmentId] ?? 0;
    const status = lineStatusFor(line, picked, tolerance, allowOversell);
    if (status === "exact") greenCount += 1;
    else if (status === "over") hasOver = true;
    else hasUnder = true;
  }
  return {
    greenCount,
    total: lines.length,
    allGreen: greenCount === lines.length && lines.length > 0,
    hasOver,
    hasUnder
  };
}
function canShip(lines, pickedByAssortment, tolerance = 0, allowOversell = true) {
  return orderProgress(lines, pickedByAssortment, tolerance, allowOversell).allGreen;
}
function visiblePicked(picked, lines, extraIds) {
  const valid = /* @__PURE__ */ new Set([...lines.map((l) => l.assortmentId), ...extraIds]);
  return Object.fromEntries(Object.entries(picked).filter(([id]) => valid.has(id)));
}
function addAmount(current, delta) {
  return round32(Math.max(0, current + delta));
}
function formatQty(n) {
  return String(round32(n));
}

// src/domain/pick-merge.ts
function round33(n) {
  return Math.round(n * 1e3) / 1e3;
}
function combinePicked(own, remote) {
  const out = { ...own };
  for (const actor of Object.values(remote)) {
    for (const [id, qty] of Object.entries(actor.picked)) {
      if (qty === 0 && !(id in out)) continue;
      out[id] = round33((out[id] ?? 0) + qty);
    }
  }
  return out;
}
function othersTotal(remote, assortmentId) {
  let sum = 0;
  for (const actor of Object.values(remote)) sum += actor.picked[assortmentId] ?? 0;
  return round33(sum);
}
function ownForSetQty(desiredCombined, others) {
  return round33(Math.max(0, desiredCombined - others));
}
function combineChecked(own, remote) {
  const out = { ...own };
  for (const actor of Object.values(remote)) {
    for (const [id, v] of Object.entries(actor.checked)) {
      if (v) out[id] = true;
    }
  }
  return out;
}
function sumContributions(rows) {
  const out = {};
  for (const row of rows) {
    if (row.qty <= 0) continue;
    out[row.assortmentId] = round33((out[row.assortmentId] ?? 0) + row.qty);
  }
  return out;
}

// src/domain/warehouse-layout.ts
function sectionRect(s) {
  return s.rotation === 90 ? { xCm: s.xCm, yCm: s.yCm, wCm: s.depthCm, hCm: s.lengthCm } : { xCm: s.xCm, yCm: s.yCm, wCm: s.lengthCm, hCm: s.depthCm };
}
function rectsOverlap(a, b) {
  return a.xCm < b.xCm + b.wCm && b.xCm < a.xCm + a.wCm && a.yCm < b.yCm + b.hCm && b.yCm < a.yCm + a.hCm;
}
function sectionsOverlap(a, b) {
  return rectsOverlap(sectionRect(a), sectionRect(b));
}
var SNAP_STEP_CM = 5;
function snapCm(v, step = SNAP_STEP_CM) {
  return Math.round(v / step) * step;
}
function clampToRoom(s, room) {
  const r = sectionRect(s);
  return {
    xCm: Math.max(0, Math.min(s.xCm, room.widthCm - r.wCm)),
    yCm: Math.max(0, Math.min(s.yCm, room.depthCm - r.hCm))
  };
}
function spotOccupancy(cell) {
  if (!cell) return "free";
  return cell.productCount > 0 ? "occupied" : "empty";
}
function levelName(level) {
  return level === 0 ? "\u041F\u043E\u043B" : `\u042F\u0440\u0443\u0441 ${level}`;
}
var section = (label, xCm, yCm, rotation = 0) => ({
  label,
  xCm,
  yCm,
  rotation,
  lengthCm: 270,
  depthCm: 105,
  levels: 3,
  spotsPerLevel: 3
});
var DEFAULT_LAYOUT = {
  widthCm: 1420,
  depthCm: 690,
  zones: [
    { kind: "buffer", xCm: 110, yCm: 240, wCm: 250, hCm: 210, label: "\u0411\u0443\u0444\u0435\u0440\u043D\u0430\u044F \u0437\u043E\u043D\u0430" },
    { kind: "gate", xCm: 260, yCm: 640, wCm: 150, hCm: 50, label: "\u0412\u043E\u0440\u043E\u0442\u0430" }
  ],
  sections: [
    // North wall row
    section("\u0410-1", 150, 0),
    section("\u0410-2", 420, 0),
    section("\u0410-3", 690, 0),
    section("\u0410-4", 960, 0),
    // Middle island, north-facing row (starts after the buffer zone)
    section("\u0411-1", 360, 240),
    section("\u0411-2", 630, 240),
    section("\u0411-3", 900, 240),
    // Middle island, south-facing row (back-to-back with Б)
    section("\u0412-1", 360, 345),
    section("\u0412-2", 630, 345),
    section("\u0412-3", 900, 345),
    // South wall row, east of the gates
    section("\u0413-1", 410, 585),
    section("\u0413-2", 680, 585),
    section("\u0413-3", 950, 585),
    // Short wall racks
    section("\u0421\u0442\u0435\u043D\u0430-\u0417", 0, 210, 90),
    section("\u0421\u0442\u0435\u043D\u0430-\u0412", 1315, 210, 90)
  ]
};
export {
  ACTING_DRIVER_HEADER,
  DEFAULT_LAYOUT,
  DEFAULT_WEIGHT_CONFIG,
  ERROR_CODES,
  PICK_SESSION_KEY_RE,
  ROLE_PERMISSIONS,
  SNAP_STEP_CM,
  abcXyzQuerySchema,
  addAmount,
  addToCartSchema,
  approveClientSchema,
  assignSpotSchema,
  auditQuerySchema,
  baseQuantityFor,
  baseToPacks,
  batchProductsSchema,
  bindProductCellSchema,
  bulkAssignDriverSchema,
  bulkCreateClientsSchema,
  bulkIdsSchema,
  bulkPriceTypeSchema,
  bulkPriceVisibilitySchema,
  bulkSetPasswordSchema,
  canShip,
  cellNameSchema,
  changeCredentialsSchema,
  changeLoginSchema,
  changePasswordSchema,
  clampToRoom,
  clientsQuerySchema,
  combineChecked,
  combinePicked,
  counterpartyIntentSchema,
  counterpartyQuerySchema,
  counterpartySearchSchema,
  createCellSchema,
  createDemandSchema,
  createGroupShipmentSchema,
  createProductSchema,
  createShipmentSchema,
  createStaffSchema,
  dailyOrdersQuerySchema,
  deadStockQuerySchema,
  effectiveTarget,
  expiryQuerySchema,
  extractShipmentSchema,
  featuredQuerySchema,
  findPack,
  formatPacks,
  formatQty,
  groupQuerySchema,
  hasPermission,
  idSchema,
  inventoryQuerySchema,
  isOrderComplete,
  kopecksSchema,
  layoutZoneSchema,
  levelName,
  lineStatus,
  lineStatusFor,
  lineTotal,
  loginSchema,
  matchScan,
  matchesSearch,
  mergeCartSchema,
  normalizeCode,
  normalizeSearch,
  notificationSettingsUpdateSchema,
  orderProgress,
  ordersQuerySchema,
  othersTotal,
  ownForSetQty,
  paginationQuerySchema,
  parsePacks,
  parseWeightBarcode,
  permissionsFor,
  pickQueueQuerySchema,
  pickSyncExtraSchema,
  pickSyncOpSchema,
  pickSyncPushSchema,
  placeOrderSchema,
  posBridgeConfigUpdateSchema,
  posBridgeSecretSchema,
  productsQuerySchema,
  profitQuerySchema,
  purchaseNormsUpdateSchema,
  purchasePlanQuerySchema,
  rankBySearch,
  receivablesQuerySchema,
  rectsOverlap,
  refreshSchema,
  registerSchema,
  rejectClientSchema,
  resolveScan,
  returnsQuerySchema,
  saveLayoutSchema,
  saveMoyskladConfigSchema,
  saveSectionSchema,
  searchScore,
  searchTokens,
  sectionRect,
  sectionsOverlap,
  setCartQtySchema,
  setCellOrderSchema,
  setCheckStockSchema,
  setHideInactiveProductsSchema,
  setPhoneSchema,
  setPickCategoryOrderSchema,
  setTokenSchema,
  shelfLifeUpdateSchema,
  shrinkageNormUpdateSchema,
  shrinkageQuerySchema,
  slugifyLogin,
  slugifyName,
  snapCm,
  snapQty,
  sortOptionSchema,
  spotOccupancy,
  staffCredentialsSchema,
  staffLoginSchema,
  staffQuerySchema,
  staffTypeSchema,
  stockState,
  sumContributions,
  suppliersQuerySchema,
  syncHistoryQuerySchema,
  telegramTokenSchema,
  transliterate,
  turnoverQuerySchema,
  uniqueLogin,
  uniqueSlug,
  unitRules,
  updateCellSchema,
  updateClientSchema,
  updateProfileSchema,
  updateStaffSchema,
  visiblePicked,
  weightWindow,
  wmsLoginSchema,
  wordSimilarity
};
//# sourceMappingURL=index.js.map